import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { fmtMoney, studentLabel, userLabel } from "@/lib/helpers";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, MagnifyingGlass, Package, Warning, PencilSimple, ArrowsLeftRight, Link, LinkBreak } from "@phosphor-icons/react";
import { FileUpload } from "@/components/ui/file-upload";
import { FileImage } from "@/components/ui/file-image";

const CATEGORIES = [
  { id: "assistive_tech",   label: "Assistive Tech",   color: "bg-purple-100 text-purple-800 border-purple-200",
    hint: "AAC devices, iPads, adaptive computers, communication boards — personal to a student." },
  { id: "therapy_material", label: "Therapy Material", color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    hint: "PECS cards, sensory bins, flashcards, weighted blankets, fidgets — shared per room." },
  { id: "consumable",       label: "Consumable",       color: "bg-amber-100 text-amber-800 border-amber-200",
    hint: "Gloves, tissues, batteries, printer paper — reorder-driven." },
  { id: "equipment",        label: "Equipment",        color: "bg-slate-100 text-slate-800 border-slate-200",
    hint: "Therapy swings, ball pits, sensory-room fixtures — long-lived shared assets." },
];
const CAT_MAP = Object.fromEntries(CATEGORIES.map(c => [c.id, c]));

const emptyForm = {
  name: "", sku: "", category: "therapy_material", unit: "each",
  branch_id: "", therapy_type_ids: [], vendor: "", cost_per_unit: 0,
  description: "", low_stock_threshold: 0, warranty_expires_on: "", notes: "",
  photo_file_id: null,
};

const MOVEMENT_REASONS = [
  { id: "receive",  label: "Received from vendor" },
  { id: "adjust",   label: "Manual adjustment (counting error)" },
  { id: "dispose",  label: "Damaged / disposed" },
  { id: "issue",    label: "Issued (given out)" },
  { id: "return",   label: "Returned to stock" },
];

export default function InventoryPage() {
  const { user, activeRole, activeBranch, branches } = useAuth();
  const canEdit = ["owner", "admin"].includes(activeRole);
  const canMoveStock = canEdit || activeRole === "reception";

  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [therapyTypes, setTherapyTypes] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [lowOnly, setLowOnly] = useState(false);

  // Dialogs
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showDetail, setShowDetail] = useState(null); // item with movements
  const [movementForm, setMovementForm] = useState({ delta: 1, reason: "receive", notes: "" });
  const [assignForm, setAssignForm] = useState({ student_id: "", therapist_id: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeBranch) params.set("branch_id", activeBranch);
      if (tab !== "all") params.set("category", tab);
      if (q.trim()) params.set("q", q.trim());
      if (lowOnly) params.set("low_stock", "true");
      const [inv, sts, tt, us] = await Promise.all([
        api.get(`/inventory?${params.toString()}`),
        api.get("/students"),
        api.get("/therapy-types"),
        api.get("/users"),
      ]);
      setItems(inv.data || []);
      setStudents(sts.data || []);
      setTherapyTypes(tt.data || []);
      setTherapists((us.data || []).filter(u => (u.roles || []).includes("therapist")));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab, activeBranch, lowOnly]);

  const studentById = useMemo(() => Object.fromEntries(students.map(s => [s.id, s])), [students]);
  const therapistById = useMemo(() => Object.fromEntries(therapists.map(t => [t.id, t])), [therapists]);
  const therapyById = useMemo(() => Object.fromEntries(therapyTypes.map(t => [t.id, t])), [therapyTypes]);

  // ---------- Item form ----------
  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, branch_id: activeBranch || (branches[0]?.id ?? "") });
    setShowForm(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      sku: item.sku || "",
      category: item.category,
      unit: item.unit || "each",
      branch_id: item.branch_id,
      therapy_type_ids: item.therapy_type_ids || [],
      vendor: item.vendor || "",
      cost_per_unit: item.cost_per_unit || 0,
      description: item.description || "",
      low_stock_threshold: item.low_stock_threshold || 0,
      warranty_expires_on: item.warranty_expires_on || "",
      notes: item.notes || "",
      photo_file_id: item.photo_file_id || null,
    });
    setShowForm(true);
  };
  const submitForm = async () => {
    if (!form.name.trim()) { toast.error("Item name is required"); return; }
    if (!form.branch_id) { toast.error("Pick a branch"); return; }
    try {
      if (editing) {
        // For edit we drop branch_id + assigned fields (immutable via edit)
        const patch = { ...form };
        delete patch.branch_id;
        await api.put(`/inventory/${editing.id}`, patch);
        toast.success("Item updated");
      } else {
        await api.post("/inventory", form);
        toast.success("Item created");
      }
      setShowForm(false); setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleTherapy = (id) => setForm(f => ({
    ...f,
    therapy_type_ids: f.therapy_type_ids.includes(id)
      ? f.therapy_type_ids.filter(x => x !== id)
      : [...f.therapy_type_ids, id],
  }));

  // ---------- Detail / movement ----------
  const openDetail = async (item) => {
    try {
      const { data } = await api.get(`/inventory/${item.id}`);
      setShowDetail(data);
      setMovementForm({ delta: 1, reason: "receive", notes: "" });
      setAssignForm({
        student_id: data.assigned_student_id || "",
        therapist_id: data.assigned_therapist_id || "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitMovement = async () => {
    if (!showDetail) return;
    const delta = Number(movementForm.delta || 0);
    if (!delta) { toast.error("Enter a non-zero quantity"); return; }
    // For "issue"/"dispose"/"return"(?) we want negative delta; for "receive"/"return"/"adjust" positive.
    // Simpler: user picks sign explicitly by using negative for out. But let's derive from reason to reduce mistakes.
    const signed = ["issue", "dispose"].includes(movementForm.reason)
      ? -Math.abs(delta)
      : ["return", "receive"].includes(movementForm.reason)
        ? Math.abs(delta)
        : delta; // adjust preserves sign
    try {
      const { data } = await api.post(`/inventory/${showDetail.id}/movements`, {
        delta: signed,
        reason: movementForm.reason,
        notes: movementForm.notes,
      });
      toast.success(`Stock updated — now ${data.qty_on_hand}`);
      openDetail(showDetail); // refresh detail
      load();                 // refresh list
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitAssign = async () => {
    if (!showDetail) return;
    try {
      const { data } = await api.post(`/inventory/${showDetail.id}/assign`, {
        student_id: assignForm.student_id || null,
        therapist_id: assignForm.therapist_id || null,
      });
      toast.success("Assignment updated");
      setShowDetail(await refetchDetail(data.id));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitUnassign = async () => {
    if (!showDetail) return;
    try {
      await api.post(`/inventory/${showDetail.id}/unassign`);
      toast.success("Unassigned");
      setShowDetail(await refetchDetail(showDetail.id));
      setAssignForm({ student_id: "", therapist_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const refetchDetail = async (id) => (await api.get(`/inventory/${id}`)).data;

  // ---------- Render ----------
  const branchName = (id) => branches.find(b => b.id === id)?.name || "—";

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-1.5">Inventory</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Assistive tech, materials &amp; equipment</h1>
          <p className="text-sm text-muted-foreground mt-1">Track what&rsquo;s on the floor across your branches &mdash; assign AAC devices to specific students, log receipts, and stay ahead of low stock.</p>
        </div>
        {canEdit && (
          <Button size="lg" onClick={openNew} className="gap-1.5 w-full sm:w-auto" data-testid="new-item-btn">
            <Plus size={16} weight="bold" /> New item
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="w-max">
            <TabsTrigger value="all" data-testid="cat-all">All</TabsTrigger>
            {CATEGORIES.map(c => (
              <TabsTrigger key={c.id} value={c.id} data-testid={`cat-${c.id}`}>{c.label}</TabsTrigger>
            ))}
          </TabsList>
        </div>
      </Tabs>

      <Card>
        <CardHeader className="gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[280px]">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                value={q}
                onChange={e => setQ(e.target.value)}
                onKeyDown={e => e.key === "Enter" && load()}
                onBlur={load}
                placeholder="Search by name, SKU or vendor…"
                className="pl-9"
                data-testid="inventory-search"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={lowOnly} onCheckedChange={v => setLowOnly(!!v)} data-testid="filter-low-stock" />
              <Warning size={14} className="text-amber-600" /> Low stock only
            </label>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading && <div className="px-5 py-8 text-sm text-muted-foreground">Loading inventory…</div>}
          {!loading && items.length === 0 && (
            <div className="px-5 py-14 text-center">
              <Package size={40} weight="duotone" className="mx-auto text-muted-foreground/60 mb-3" />
              <div className="text-sm text-muted-foreground">
                {q || lowOnly || tab !== "all" ? "No items match your filters." : "No inventory yet &mdash; add your first item to get started."}
              </div>
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="divide-y divide-border">
              {items.map(it => {
                const cat = CAT_MAP[it.category];
                const low = (it.low_stock_threshold || 0) > 0 && (it.qty_on_hand || 0) <= (it.low_stock_threshold || 0);
                const student = it.assigned_student_id && studentById[it.assigned_student_id];
                const therapist = it.assigned_therapist_id && therapistById[it.assigned_therapist_id];
                return (
                  <button
                    key={it.id}
                    onClick={() => openDetail(it)}
                    className="w-full px-5 py-3 flex items-center gap-4 hover:bg-muted/40 text-left"
                    data-testid={`item-row-${it.id}`}
                  >
                    <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                      {it.photo_file_id ? (
                        <FileImage
                          fileId={it.photo_file_id}
                          alt=""
                          className="h-10 w-10 object-cover"
                          fallback={<Package size={18} weight="duotone" className="text-muted-foreground" />}
                        />
                      ) : (
                        <Package size={18} weight="duotone" className="text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-2 flex-wrap">
                        {it.name}
                        {cat && <Badge className={`text-[10px] border ${cat.color}`}>{cat.label}</Badge>}
                        {low && <Badge className="text-[10px] bg-red-100 text-red-800 border-red-200">Low stock</Badge>}
                        {student && <Badge variant="outline" className="text-[10px]">→ {student.name}</Badge>}
                        {therapist && <Badge variant="outline" className="text-[10px]">→ {therapist.name}</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {it.sku && <span className="font-mono">{it.sku} · </span>}
                        {branchName(it.branch_id)}
                        {it.vendor && ` · ${it.vendor}`}
                        {it.cost_per_unit > 0 && ` · ${fmtMoney(it.cost_per_unit)} / ${it.unit}`}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`font-display text-2xl font-bold tabular-nums ${low ? "text-red-600" : ""}`}>
                        {it.qty_on_hand ?? 0}
                      </div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{it.unit || "each"}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---------------- New / edit item dialog ---------------- */}
      <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) setEditing(null); }}>
        <DialogContent
          className="bg-card w-[calc(100vw-1.5rem)] sm:w-full max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
          data-testid="inventory-item-dialog"
        >
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Edit item" : "New inventory item"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="pb-3 border-b border-border">
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Item photo <span className="text-muted-foreground normal-case tracking-normal">(optional)</span></Label>
              <div className="mt-2">
                <FileUpload
                  value={form.photo_file_id}
                  onChange={(id) => setForm(f => ({ ...f, photo_file_id: id }))}
                  entityType="inventory_item"
                  entityId={editing?.id}
                  accept="image/jpeg,image/png,image/webp"
                  variant="avatar"
                  size={80}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Proloquo2Go iPad" data-testid="form-item-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="form-item-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">
                    {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-muted-foreground">{CAT_MAP[form.category]?.hint}</div>
              </div>
              <div className="space-y-1.5">
                <Label>Branch</Label>
                <Select value={form.branch_id} onValueChange={v => setForm({ ...form, branch_id: v })} disabled={!!editing}>
                  <SelectTrigger data-testid="form-item-branch"><SelectValue placeholder="Choose branch" /></SelectTrigger>
                  <SelectContent className="bg-card">
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>SKU <span className="text-[10px] text-muted-foreground">(optional)</span></Label>
                <Input value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} className="font-mono" placeholder="e.g. AT-IPAD-001" />
              </div>
              <div className="space-y-1.5">
                <Label>Unit</Label>
                <Input value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} placeholder="each, pack, box, kg" />
              </div>
              <div className="space-y-1.5">
                <Label>Cost per unit</Label>
                <Input type="number" step="0.01" value={form.cost_per_unit} onChange={e => setForm({ ...form, cost_per_unit: Number(e.target.value) })} />
              </div>
              <div className="space-y-1.5">
                <Label>Vendor</Label>
                <Input value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} placeholder="Supplier name" />
              </div>
              <div className="space-y-1.5">
                <Label>Low-stock threshold</Label>
                <Input type="number" value={form.low_stock_threshold} onChange={e => setForm({ ...form, low_stock_threshold: Number(e.target.value) })} />
              </div>
              <div className="space-y-1.5">
                <Label>Warranty expiry <span className="text-[10px] text-muted-foreground">(optional)</span></Label>
                <Input type="date" value={form.warranty_expires_on} onChange={e => setForm({ ...form, warranty_expires_on: e.target.value })} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Used for these therapies <span className="text-[10px] text-muted-foreground">(optional)</span></Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {therapyTypes.map(t => (
                    <label key={t.id} className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-sm ${form.therapy_type_ids.includes(t.id) ? "border-primary bg-primary/5" : "border-border"}`}>
                      <Checkbox checked={form.therapy_type_ids.includes(t.id)} onCheckedChange={() => toggleTherapy(t.id)} />
                      {t.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Description / notes</Label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  className="w-full text-sm rounded-md border border-border p-3 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={submitForm} data-testid="save-item">{editing ? "Save changes" : "Create item"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Item detail drawer ---------------- */}
      <Dialog open={!!showDetail} onOpenChange={(o) => { if (!o) setShowDetail(null); }}>
        <DialogContent className="bg-card max-w-3xl max-h-[90vh] overflow-y-auto">
          {showDetail && <ItemDetail
            item={showDetail}
            catMap={CAT_MAP}
            branchName={branchName}
            students={students}
            therapists={therapists}
            therapyById={therapyById}
            movementForm={movementForm}
            setMovementForm={setMovementForm}
            assignForm={assignForm}
            setAssignForm={setAssignForm}
            canEdit={canEdit}
            canMoveStock={canMoveStock}
            onEdit={() => { setShowDetail(null); openEdit(showDetail); }}
            onMovement={submitMovement}
            onAssign={submitAssign}
            onUnassign={submitUnassign}
          />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ItemDetail — inlined child to keep this page in one file for now.
// ---------------------------------------------------------------------------
function ItemDetail({
  item, catMap, branchName, students, therapists, therapyById,
  movementForm, setMovementForm, assignForm, setAssignForm,
  canEdit, canMoveStock, onEdit, onMovement, onAssign, onUnassign,
}) {
  const cat = catMap[item.category];
  const low = (item.low_stock_threshold || 0) > 0 && (item.qty_on_hand || 0) <= (item.low_stock_threshold || 0);
  const student = students.find(s => s.id === item.assigned_student_id);
  const therapist = therapists.find(t => t.id === item.assigned_therapist_id);

  return (
    <>
      <DialogHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-14 w-14 rounded-md bg-muted flex items-center justify-center shrink-0 overflow-hidden">
              {item.photo_file_id ? (
                <FileImage
                  fileId={item.photo_file_id}
                  alt=""
                  className="h-14 w-14 object-cover"
                  fallback={<Package size={22} weight="duotone" className="text-muted-foreground" />}
                />
              ) : (
                <Package size={22} weight="duotone" className="text-muted-foreground" />
              )}
            </div>
            <div className="min-w-0">
              <DialogTitle className="font-display tracking-tight text-2xl">{item.name}</DialogTitle>
              <div className="text-xs text-muted-foreground mt-1 flex flex-wrap items-center gap-2">
                <Badge className={`text-[10px] border ${cat?.color}`}>{cat?.label}</Badge>
                {item.sku && <span className="font-mono">{item.sku}</span>}
                <span>· {branchName(item.branch_id)}</span>
                {item.vendor && <span>· {item.vendor}</span>}
              </div>
            </div>
          </div>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={onEdit} className="gap-1.5" data-testid="edit-item-btn">
              <PencilSimple size={14} /> Edit details
            </Button>
          )}
        </div>
      </DialogHeader>

      <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-2">
        <div className={`p-3 rounded-md border ${low ? "border-red-200 bg-red-50" : "border-border"}`}>
          <div className="overline">On hand</div>
          <div className={`font-display text-3xl font-bold tabular-nums mt-1 ${low ? "text-red-600" : ""}`}>{item.qty_on_hand ?? 0}</div>
          <div className="text-[11px] text-muted-foreground">{item.unit || "each"}</div>
        </div>
        <div className="p-3 rounded-md border border-border">
          <div className="overline">Low-stock at</div>
          <div className="font-display text-3xl font-bold tabular-nums mt-1">{item.low_stock_threshold || 0}</div>
          <div className="text-[11px] text-muted-foreground">alert threshold</div>
        </div>
        <div className="p-3 rounded-md border border-border">
          <div className="overline">Unit cost</div>
          <div className="font-display text-3xl font-bold tabular-nums mt-1">{item.cost_per_unit ? fmtMoney(item.cost_per_unit) : "—"}</div>
          <div className="text-[11px] text-muted-foreground">per {item.unit || "each"}</div>
        </div>
      </div>

      {item.warranty_expires_on && (
        <div className="text-xs text-muted-foreground mt-3">Warranty expires: <span className="font-medium text-foreground">{item.warranty_expires_on}</span></div>
      )}
      {(item.therapy_type_ids || []).length > 0 && (
        <div className="flex gap-1.5 flex-wrap mt-3">
          {item.therapy_type_ids.map(id => (
            <Badge key={id} variant="outline" className="text-[10px]">{therapyById[id]?.name || id}</Badge>
          ))}
        </div>
      )}
      {item.notes && <div className="text-sm text-muted-foreground mt-3 whitespace-pre-wrap">{item.notes}</div>}

      {/* Assignment (personal AT devices) */}
      {canEdit && item.category === "assistive_tech" && (
        <div className="mt-5 p-4 rounded-md border border-border bg-muted/30">
          <div className="flex items-center gap-2 text-sm font-medium mb-2">
            <Link size={14} weight="bold" /> Personal assignment
          </div>
          {(student || therapist) ? (
            <div className="text-sm">
              Currently with{" "}
              {student && <Badge variant="outline" className="text-[10px] mr-1">Student · {student.name}</Badge>}
              {therapist && <Badge variant="outline" className="text-[10px]">Therapist · {therapist.name}</Badge>}
              <Button variant="ghost" size="sm" onClick={onUnassign} className="ml-2 gap-1.5 text-red-600 hover:text-red-700" data-testid="unassign-btn">
                <LinkBreak size={14} /> Unassign
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Assign to student</Label>
                <Select value={assignForm.student_id || "none"} onValueChange={v => setAssignForm(a => ({ ...a, student_id: v === "none" ? "" : v, therapist_id: "" }))}>
                  <SelectTrigger data-testid="assign-student"><SelectValue placeholder="Pick a student" /></SelectTrigger>
                  <SelectContent className="bg-card">
                    <SelectItem value="none">— None —</SelectItem>
                    {students.map(s => <SelectItem key={s.id} value={s.id}>{studentLabel(s)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">…or assign to therapist</Label>
                <Select value={assignForm.therapist_id || "none"} onValueChange={v => setAssignForm(a => ({ ...a, therapist_id: v === "none" ? "" : v, student_id: "" }))}>
                  <SelectTrigger data-testid="assign-therapist"><SelectValue placeholder="Pick a therapist" /></SelectTrigger>
                  <SelectContent className="bg-card">
                    <SelectItem value="none">— None —</SelectItem>
                    {therapists.map(t => <SelectItem key={t.id} value={t.id}>{userLabel(t)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-1 sm:col-span-2">
                <Button size="sm" onClick={onAssign} disabled={!assignForm.student_id && !assignForm.therapist_id} data-testid="submit-assign">Assign</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Movement form */}
      {canMoveStock && (
        <div className="mt-5 p-4 rounded-md border border-border">
          <div className="flex items-center gap-2 text-sm font-medium mb-3">
            <ArrowsLeftRight size={14} weight="bold" /> Record stock movement
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs">Reason</Label>
              <Select value={movementForm.reason} onValueChange={v => setMovementForm(m => ({ ...m, reason: v }))}>
                <SelectTrigger data-testid="mv-reason"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card">
                  {MOVEMENT_REASONS.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Quantity</Label>
              <Input type="number" value={movementForm.delta} onChange={e => setMovementForm(m => ({ ...m, delta: Number(e.target.value) }))} data-testid="mv-qty" />
            </div>
            <Button onClick={onMovement} data-testid="mv-submit">Record</Button>
            <div className="space-y-1.5 col-span-2 sm:col-span-4">
              <Label className="text-xs">Notes <span className="text-muted-foreground">(optional)</span></Label>
              <Input value={movementForm.notes} onChange={e => setMovementForm(m => ({ ...m, notes: e.target.value }))} placeholder="Vendor invoice #, condition, receiver, etc." />
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground mt-2">
            &ldquo;Received&rdquo; and &ldquo;Returned&rdquo; add to stock. &ldquo;Issued&rdquo; and &ldquo;Disposed&rdquo; subtract. &ldquo;Adjust&rdquo; uses the sign of the number you enter (use negative to reduce).
          </div>
        </div>
      )}

      {/* Movement log */}
      <div className="mt-5">
        <div className="overline mb-2">Movement history ({(item.movements || []).length})</div>
        {(item.movements || []).length === 0 ? (
          <div className="text-sm text-muted-foreground italic">No movements yet.</div>
        ) : (
          <div className="divide-y divide-border border border-border rounded-md">
            {item.movements.map(m => (
              <div key={m.id} className="px-3 py-2 flex items-center gap-3 text-sm">
                <div className={`h-8 w-14 rounded flex items-center justify-center font-mono font-bold text-sm ${m.delta > 0 ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                  {m.delta > 0 ? "+" : ""}{m.delta}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium capitalize">{m.reason.replace("_", " ")}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {(m.at || "").replace("T", " ").slice(0, 16)} · {m.actor_name || m.actor_id}
                    {m.notes && ` · ${m.notes}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
